import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Camera, Upload, Trash2, ExternalLink, FileText, Loader2, Wand2 } from "lucide-react";
import { API, selectCls, dstr, eur, btnGhost } from "./shared";

export const Receipts = () => {
  const [receipts, setReceipts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [filter, setFilter] = useState("alle");
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(null);
  const camRef = useRef(null);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    axios.get(`${API}/receipts`, { withCredentials: true }).then(({ data }) => setReceipts(data)).catch(() => {});
    axios.get(`${API}/transactions`, { withCredentials: true }).then(({ data }) => setTransactions(data)).catch(() => {});
    axios.get(`${API}/invoices`, { withCredentials: true }).then(({ data }) => setInvoices(data)).catch(() => {});
  }, []);

  useEffect(load, [load]);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await axios.post(`${API}/receipts`, fd, { withCredentials: true });
      if (data.transaction_id) {
        toast.success(`Beleg automatisch zugeordnet (Datum ${data.doc_date ? new Date(data.doc_date).toLocaleDateString("de-DE") : "Übereinstimmung"}).`);
      } else {
        toast.success("Beleg hochgeladen – jetzt unten einer Buchung zuordnen.");
      }
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload fehlgeschlagen.");
    } finally {
      setUploading(false);
    }
  };

  const assign = async (id, patch) => {
    try {
      const { data } = await axios.patch(`${API}/receipts/${id}`, patch, { withCredentials: true });
      setReceipts((rs) => rs.map((r) => (r.id === id ? data : r)));
      toast.success("Beleg gespeichert.");
    } catch {
      toast.error("Zuordnung fehlgeschlagen.");
    }
  };

  const del = async (id) => {
    await axios.delete(`${API}/receipts/${id}`, { withCredentials: true }).catch(() => toast.error("Löschen fehlgeschlagen."));
    setReceipts((rs) => rs.filter((r) => r.id !== id));
  };

  const analyze = async (id) => {
    setAnalyzing(id);
    try {
      const { data } = await axios.post(`${API}/receipts/${id}/analyze`, {}, { withCredentials: true, timeout: 90000 });
      const a = data.analysis || {};
      const parts = [];
      if (a.vendor) parts.push(a.vendor);
      if (a.doc_number) parts.push(`Nr. ${a.doc_number}`);
      if (a.doc_date) parts.push(dstr(a.doc_date));
      if (a.net_amount) parts.push(`${eur(a.net_amount)} netto`);
      toast.success(
        parts.length
          ? `KI erkannt: ${parts.join(" · ")}${data.transaction_created ? " – Buchung wurde automatisch angelegt und verknüpft." : ""}`
          : "KI konnte leider nichts Eindeutiges erkennen – bitte manuell eintragen."
      );
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "KI-Erkennung fehlgeschlagen.");
    } finally {
      setAnalyzing(null);
    }
  };

  const shown = receipts.filter((r) =>
    filter === "alle" ? true : filter === "frei" ? !r.transaction_id && !r.invoice_id : r.transaction_id || r.invoice_id
  );
  const txLabel = (id) => {
    const t = transactions.find((x) => x.id === id);
    return t ? `${dstr(t.date)} · ${t.description}` : "";
  };

  return (
    <div data-testid="receipts-view">
      {/* Upload */}
      <div className="bg-[#1e2226] border border-white/10 rounded-2xl p-6 mb-6" data-testid="receipt-upload-card">
        <p className="font-display text-white/40 text-xs uppercase tracking-[0.2em] mb-2">Beleg erfassen</p>
        <p className="text-white/50 text-xs mb-5">Foto vom Handy aufnehmen oder PDF/Bild hochladen. Bei Fotos wird das Aufnahmedatum automatisch erkannt und der Beleg der Buchung mit demselben Datum zugeordnet – du kannst alles unten noch ändern.</p>
        <div className="flex flex-wrap gap-3">
          <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" data-testid="receipt-camera-input" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
          <input ref={fileRef} type="file" accept=".pdf,image/*" className="hidden" data-testid="receipt-file-input" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
          <button data-testid="receipt-camera" onClick={() => camRef.current?.click()} disabled={uploading} className="inline-flex items-center gap-2 bg-[#e2001a] hover:bg-[#b80016] disabled:opacity-50 px-5 py-3 rounded-xl text-sm font-display font-semibold transition-colors">
            {uploading ? <Loader2 size={15} className="animate-spin" /> : <Camera size={15} />} Foto aufnehmen
          </button>
          <button data-testid="receipt-upload" onClick={() => fileRef.current?.click()} disabled={uploading} className={`${btnGhost} !py-3`}>
            <Upload size={14} /> Datei hochladen (PDF/JPG/PNG)
          </button>
        </div>
      </div>

      {/* Filter */}
      <div className="flex gap-2 mb-4">
        {[["alle", "Alle"], ["frei", "Nicht zugeordnet"], ["zugeordnet", "Zugeordnet"]].map(([k, label]) => (
          <button
            key={k}
            data-testid={`receipt-filter-${k}`}
            onClick={() => setFilter(k)}
            className={`px-4 py-2 rounded-full border font-display text-xs font-medium transition-colors ${
              filter === k ? "bg-[#e2001a] border-[#e2001a] text-white" : "border-white/20 text-white/55 hover:text-white"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {shown.length === 0 ? (
        <div data-testid="receipts-empty" className="bg-[#1e2226] border border-white/10 rounded-2xl p-14 text-center text-white/50">
          Noch keine Belege – fotografiere oder lade oben den ersten hoch.
        </div>
      ) : (
        <div data-testid="receipts-list" className="grid sm:grid-cols-2 gap-4">
          {shown.map((r) => (
            <div key={r.id} data-testid={`receipt-${r.id}`} className="bg-[#1e2226] border border-white/10 rounded-2xl p-4">
              <div className="flex gap-4">
                {/* Vorschau */}
                <a href={`${API}/receipts/${r.id}/download`} target="_blank" rel="noreferrer" className="shrink-0">
                  {r.content_type?.startsWith("image/") ? (
                    <img src={`${API}/receipts/${r.id}/download`} alt={r.filename} className="w-20 h-20 object-cover rounded-lg border border-white/10" />
                  ) : (
                    <div className="w-20 h-20 rounded-lg border border-white/10 bg-white/5 flex items-center justify-center">
                      <FileText size={26} className="text-white/40" />
                    </div>
                  )}
                </a>
                <div className="flex-1 min-w-0">
                  <p className="font-display font-medium text-sm truncate" title={r.filename}>{r.filename}</p>
                  <p className="text-white/40 text-xs mt-0.5">hochgeladen {dstr(r.uploaded_at)} · {(r.size / 1024).toFixed(0)} KB</p>
                  <div className="flex items-center gap-2 mt-1.5">
                    <label className="text-white/40 text-[11px]">Belegdatum</label>
                    <input
                      data-testid={`receipt-date-${r.id}`}
                      type="date"
                      value={r.doc_date || ""}
                      onChange={(e) => assign(r.id, { doc_date: e.target.value })}
                      className="bg-white/5 border border-white/15 rounded-lg px-2 py-1 text-white text-[11px] focus:outline-none focus:border-[#e2001a] [color-scheme:dark]"
                    />
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    <button
                      data-testid={`receipt-analyze-${r.id}`}
                      onClick={() => analyze(r.id)}
                      disabled={analyzing === r.id}
                      className="inline-flex items-center gap-1.5 bg-[#e2001a] hover:bg-[#b80016] disabled:opacity-50 px-3 py-1.5 rounded-xl text-xs font-display font-semibold transition-colors"
                      title="Betrag, Datum und Lieferant per KI auslesen"
                    >
                      {analyzing === r.id ? <Loader2 size={12} className="animate-spin" /> : <Wand2 size={12} />} KI-Erkennung
                    </button>
                    <a data-testid={`receipt-open-${r.id}`} href={`${API}/receipts/${r.id}/download`} target="_blank" rel="noreferrer" className={`${btnGhost} !px-3 !py-1.5 !text-xs`}>
                      <ExternalLink size={12} /> Öffnen
                    </a>
                    <button data-testid={`receipt-delete-${r.id}`} onClick={() => del(r.id)} className="text-white/30 hover:text-[#ff4d5e] transition-colors p-1.5" aria-label="Beleg löschen">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
              {/* Zuordnung */}
              <div className="grid grid-cols-1 gap-2 mt-3">
                <div className="grid grid-cols-2 gap-2">
                  <input
                    data-testid={`receipt-doc-number-${r.id}`}
                    defaultValue={r.doc_number || ""}
                    onBlur={(e) => { if (e.target.value !== (r.doc_number || "")) assign(r.id, { doc_number: e.target.value }); }}
                    placeholder="Rechnungsnr. auf dem Beleg"
                    className={`${selectCls} !py-2 !text-xs`}
                  />
                  <input
                    data-testid={`receipt-vendor-${r.id}`}
                    defaultValue={r.vendor || ""}
                    onBlur={(e) => { if (e.target.value !== (r.vendor || "")) assign(r.id, { vendor: e.target.value }); }}
                    placeholder="Lieferant / Kunde"
                    className={`${selectCls} !py-2 !text-xs`}
                  />
                </div>
                <select
                  data-testid={`receipt-kind-${r.id}`}
                  value={r.kind_label || ""}
                  onChange={(e) => assign(r.id, { kind_label: e.target.value })}
                  className={`${selectCls} !py-2 !text-xs`}
                >
                  <option value="">Vorgangsart wählen …</option>
                  {["Wareneinkauf", "Dienstleistung", "Verkauf", "Sonstiges"].map((k) => <option key={k} value={k}>{k}</option>)}
                </select>
                <input
                  data-testid={`receipt-note-${r.id}`}
                  defaultValue={r.note || ""}
                  onBlur={(e) => { if (e.target.value !== (r.note || "")) assign(r.id, { note: e.target.value }); }}
                  placeholder="Notiz für die Steuerberaterin (optional)"
                  className={`${selectCls} !py-2 !text-xs`}
                />
                <select
                  data-testid={`receipt-assign-tx-${r.id}`}
                  value={r.transaction_id || ""}
                  onChange={(e) => assign(r.id, { transaction_id: e.target.value })}
                  className={`${selectCls} !py-2 !text-xs ${!r.transaction_id ? "border-amber-400/40" : "border-emerald-400/40"}`}
                >
                  <option value="">Buchung zuordnen …</option>
                  {transactions.map((t) => (
                    <option key={t.id} value={t.id}>{t.kind === "einnahme" ? "+" : "−"} {dstr(t.date)} · {t.description}</option>
                  ))}
                </select>
                <select
                  data-testid={`receipt-assign-inv-${r.id}`}
                  value={r.invoice_id || ""}
                  onChange={(e) => assign(r.id, { invoice_id: e.target.value })}
                  className={`${selectCls} !py-2 !text-xs`}
                >
                  <option value="">Rechnung zuordnen (optional) …</option>
                  {invoices.map((i) => (
                    <option key={i.id} value={i.id}>{i.number} · {i.customer_name}</option>
                  ))}
                </select>
                {r.transaction_id && <p className="text-emerald-300/80 text-[11px]">Zugeordnet: {txLabel(r.transaction_id)}</p>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
